import React from 'react';
import {
  Loader2,
  CheckCircle2,
  XCircle,
  AlertCircle,
  AlertTriangle,
  Info,
  ChevronDown,
  ChevronUp,
  Sparkles,
  ArrowRight,
  RefreshCw,
  X,
  StopCircle,
} from 'lucide-react';
import type {
  AuditStatus,
  AuditReport,
  AuditPageResult,
  FixResult,
  FixProgressItem,
} from '../../hooks/useRoarySEOAudit';
import roaryMascot from '/assets/Roary-mascot.png';

interface RoarySEOAuditPanelProps {
  status: AuditStatus;
  auditReport: AuditReport | null;
  fixResult: FixResult | null;
  fixProgress: FixProgressItem[];
  error: string | null;
  analyzedPages: Array<{ pageTitle: string; score: number }>;
  onStartAudit: () => void;
  onStartFix: (auditId: string) => void;
  onCancel: () => void;
  onReset: () => void;
  onClose: () => void;
  isDepleted?: boolean;
}

// ─── Helpers ───────────────────────────────────────────────────

const getScoreColor = (score: number) => {
  if (score >= 80) return 'text-green-600';
  if (score >= 60) return 'text-yellow-600';
  return 'text-red-600';
};

const getScoreBg = (score: number) => {
  if (score >= 80) return 'bg-green-100';
  if (score >= 60) return 'bg-yellow-100';
  return 'bg-red-100';
};

const IssueIcon: React.FC<{ type: string; className?: string }> = ({
  type,
  className = 'w-4 h-4',
}) => {
  if (type === 'error') return <XCircle className={`${className} text-red-500`} />;
  if (type === 'warning') return <AlertTriangle className={`${className} text-yellow-500`} />;
  return <Info className={`${className} text-blue-500`} />;
};

// ─── Idle View ─────────────────────────────────────────────────

const IdleView: React.FC<{ onStart: () => void; isDepleted?: boolean }> = ({
  onStart,
  isDepleted,
}) => (
  <div className="flex flex-col items-center justify-center py-12 px-6 text-center">
    <img src={roaryMascot} alt="Roary" className="w-20 h-20 object-contain mb-4" />
    <h3 className="text-lg font-semibold text-gray-900 mb-2">Roary SEO Inspector</h3>
    <p className="text-sm text-gray-600 mb-6 max-w-md">
      Let Roary analyze your site's SEO health and automatically fix issues like missing meta
      descriptions, image alt text, and heading structure.
    </p>
    <button
      onClick={onStart}
      disabled={isDepleted}
      className="flex items-center gap-2 px-6 py-3 bg-orange-500 text-white rounded-xl font-medium hover:bg-orange-600 transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed"
    >
      <Sparkles className="w-5 h-5" />
      Run SEO Audit
    </button>
    {isDepleted && (
      <p className="text-xs text-red-500 mt-3">Out of marshmallows — resets next billing cycle</p>
    )}
  </div>
);

// ─── Auditing View ─────────────────────────────────────────────

const AuditingView: React.FC<{
  analyzedPages: Array<{ pageTitle: string; score: number }>;
  onCancel: () => void;
}> = ({ analyzedPages, onCancel }) => (
  <div className="py-8 px-6">
    <div className="flex items-center gap-3 mb-6">
      <img src={roaryMascot} alt="Roary" className="w-10 h-10 object-contain animate-pulse" />
      <div>
        <h3 className="text-lg font-semibold text-gray-900">Analyzing your site...</h3>
        <p className="text-sm text-gray-500">Roary is inspecting each page for SEO issues</p>
      </div>
    </div>

    {analyzedPages.length > 0 && (
      <div className="space-y-2 mb-6">
        {analyzedPages.map((page, i) => (
          <div key={i} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
            <div className="flex items-center gap-2">
              <CheckCircle2 className="w-4 h-4 text-green-500" />
              <span className="text-sm text-gray-700">{page.pageTitle}</span>
            </div>
            <span className={`text-sm font-semibold ${getScoreColor(page.score)}`}>
              {page.score}/100
            </span>
          </div>
        ))}
      </div>
    )}

    <div className="flex items-center gap-2 text-sm text-gray-500">
      <Loader2 className="w-4 h-4 animate-spin" />
      <span>Analyzing pages...</span>
    </div>

    <button
      onClick={onCancel}
      className="mt-4 flex items-center gap-2 px-4 py-2 text-sm text-gray-600 hover:text-gray-900 transition-colors"
    >
      <StopCircle className="w-4 h-4" />
      Cancel
    </button>
  </div>
);

// ─── Report View ───────────────────────────────────────────────

const PageIssues: React.FC<{ page: AuditPageResult }> = ({ page }) => {
  const [expanded, setExpanded] = React.useState(false);

  return (
    <div className="border border-gray-200 rounded-lg overflow-hidden">
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between p-4 hover:bg-gray-50 transition-colors"
      >
        <div className="flex items-center gap-3">
          <div
            className={`px-2 py-1 rounded text-xs font-semibold ${getScoreBg(page.score)} ${getScoreColor(page.score)}`}
          >
            {page.score}/100
          </div>
          <div className="text-left">
            <p className="text-sm font-medium text-gray-900">{page.title}</p>
            <p className="text-xs text-gray-500">
              {page.issues.length} issue{page.issues.length !== 1 ? 's' : ''}
            </p>
          </div>
        </div>
        {expanded ? (
          <ChevronUp className="w-4 h-4 text-gray-400" />
        ) : (
          <ChevronDown className="w-4 h-4 text-gray-400" />
        )}
      </button>

      {expanded && page.issues.length > 0 && (
        <div className="border-t border-gray-100 p-4 space-y-2">
          {page.issues.map((issue, i) => (
            <div key={i} className="flex items-start gap-2 text-sm">
              <IssueIcon type={issue.type} className="w-4 h-4 mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-gray-800">{issue.message}</p>
                {issue.recommendation && (
                  <p className="text-xs text-gray-500 mt-0.5">{issue.recommendation}</p>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const ReportView: React.FC<{
  report: AuditReport;
  onStartFix: () => void;
  onReset: () => void;
  isDepleted?: boolean;
}> = ({ report, onStartFix, onReset, isDepleted }) => (
  <div className="py-6 px-6 space-y-6">
    {/* Score overview */}
    <div className="flex items-center gap-4">
      <div
        className={`w-16 h-16 rounded-full flex items-center justify-center text-xl font-bold ${getScoreBg(report.overallScore)} ${getScoreColor(report.overallScore)}`}
      >
        {report.overallScore}
      </div>
      <div>
        <h3 className="text-lg font-semibold text-gray-900">
          SEO Score: {report.overallScore}/100
        </h3>
        <p className="text-sm text-gray-500">
          {report.pages.length} page{report.pages.length !== 1 ? 's' : ''} analyzed
        </p>
      </div>
    </div>

    {/* Summary */}
    <div className="flex items-start gap-3 p-4 bg-orange-50 rounded-lg border border-orange-100">
      <img src={roaryMascot} alt="Roary" className="w-8 h-8 object-contain flex-shrink-0" />
      <p className="text-sm text-gray-700 leading-relaxed">{report.summary}</p>
    </div>

    {/* Stats */}
    <div className="grid grid-cols-2 gap-3">
      <div className="bg-gray-50 rounded-lg p-3 text-center">
        <p className="text-2xl font-bold text-gray-900">
          {report.fixes.length + report.manualActions.length}
        </p>
        <p className="text-xs text-gray-500">Total Issues</p>
      </div>
      <div className="bg-gray-50 rounded-lg p-3 text-center">
        <p className="text-2xl font-bold text-orange-600">{report.fixableCount}</p>
        <p className="text-xs text-gray-500">Auto-fixable</p>
      </div>
    </div>

    {/* Pages breakdown */}
    <div className="space-y-2">
      <h4 className="text-sm font-semibold text-gray-900">Pages</h4>
      {report.pages.map(page => (
        <PageIssues key={page.pageId} page={page} />
      ))}
    </div>

    {/* Manual actions */}
    {report.manualActions.length > 0 && (
      <div>
        <h4 className="text-sm font-semibold text-gray-900 mb-2">Manual Actions Needed</h4>
        <div className="space-y-2">
          {report.manualActions.map((action, i) => (
            <div key={i} className="flex items-start gap-2 text-sm p-3 bg-gray-50 rounded-lg">
              <AlertCircle className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-gray-700">{action.description}</p>
                <p className="text-xs text-gray-500 mt-0.5">{action.recommendation}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    )}

    {/* Actions */}
    <div className="flex gap-3 pt-2">
      {report.fixableCount > 0 && (
        <button
          onClick={onStartFix}
          disabled={isDepleted}
          className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-orange-500 text-white rounded-xl font-medium hover:bg-orange-600 transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed"
        >
          <Sparkles className="w-5 h-5" />
          Let Roary Fix It ({report.fixableCount} issues)
        </button>
      )}
      <button
        onClick={onReset}
        className="px-4 py-3 text-gray-600 hover:text-gray-900 rounded-xl border border-gray-200 hover:bg-gray-50 transition-colors"
      >
        <RefreshCw className="w-5 h-5" />
      </button>
    </div>
    {isDepleted && report.fixableCount > 0 && (
      <p className="text-xs text-red-500 text-center">
        Out of marshmallows — resets next billing cycle
      </p>
    )}
  </div>
);

// ─── Fixing View ───────────────────────────────────────────────

const FixingView: React.FC<{
  progress: FixProgressItem[];
  totalFixes: number;
  onCancel: () => void;
}> = ({ progress, totalFixes, onCancel }) => {
  const completed = progress.filter(p => p.status === 'completed').length;
  const failed = progress.filter(p => p.status === 'failed').length;

  return (
    <div className="py-6 px-6">
      <div className="flex items-center gap-3 mb-6">
        <img src={roaryMascot} alt="Roary" className="w-10 h-10 object-contain" />
        <div>
          <h3 className="text-lg font-semibold text-gray-900">Fixing SEO issues...</h3>
          <p className="text-sm text-gray-500">
            {completed + failed} of {totalFixes} fixes applied
          </p>
        </div>
      </div>

      {/* Progress bar */}
      <div className="w-full bg-gray-200 rounded-full h-2 mb-6">
        <div
          className="bg-orange-500 h-2 rounded-full transition-all duration-300"
          style={{ width: `${totalFixes > 0 ? ((completed + failed) / totalFixes) * 100 : 0}%` }}
        />
      </div>

      {/* Fix items */}
      <div className="space-y-2 max-h-96 overflow-y-auto">
        {progress.map((item, i) => (
          <div key={i} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg text-sm">
            {item.status === 'in_progress' && (
              <Loader2 className="w-4 h-4 animate-spin text-orange-500 flex-shrink-0" />
            )}
            {item.status === 'completed' && (
              <CheckCircle2 className="w-4 h-4 text-green-500 flex-shrink-0" />
            )}
            {item.status === 'failed' && <XCircle className="w-4 h-4 text-red-500 flex-shrink-0" />}
            <span className="text-gray-700">{item.description}</span>
          </div>
        ))}
      </div>

      <button
        onClick={onCancel}
        className="mt-4 flex items-center gap-2 px-4 py-2 text-sm text-gray-600 hover:text-gray-900 transition-colors"
      >
        <StopCircle className="w-4 h-4" />
        Stop
      </button>
    </div>
  );
};

// ─── Complete View ─────────────────────────────────────────────

const CompleteView: React.FC<{
  result: FixResult;
  onReset: () => void;
}> = ({ result, onReset }) => {
  const improved = result.afterScore > result.beforeScore;

  return (
    <div className="py-8 px-6 text-center">
      <img src={roaryMascot} alt="Roary" className="w-16 h-16 object-contain mx-auto mb-4" />
      <h3 className="text-lg font-semibold text-gray-900 mb-2">
        {result.fixedCount > 0 ? 'SEO Issues Fixed!' : 'Audit Complete'}
      </h3>

      {/* Before / After */}
      <div className="flex items-center justify-center gap-4 my-6">
        <div className="text-center">
          <p className="text-xs text-gray-500 mb-1">Before</p>
          <p className={`text-3xl font-bold ${getScoreColor(result.beforeScore)}`}>
            {result.beforeScore}
          </p>
        </div>
        <ArrowRight className="w-6 h-6 text-gray-300" />
        <div className="text-center">
          <p className="text-xs text-gray-500 mb-1">After</p>
          <p className={`text-3xl font-bold ${getScoreColor(result.afterScore)}`}>
            {result.afterScore}
          </p>
        </div>
      </div>

      {improved && (
        <p className="text-sm text-green-600 mb-4">
          +{result.afterScore - result.beforeScore} points improvement!
        </p>
      )}

      <div className="flex items-center justify-center gap-4 text-sm text-gray-600 mb-6">
        <span className="flex items-center gap-1">
          <CheckCircle2 className="w-4 h-4 text-green-500" />
          {result.fixedCount} fixed
        </span>
        {result.failedCount > 0 && (
          <span className="flex items-center gap-1">
            <XCircle className="w-4 h-4 text-red-500" />
            {result.failedCount} failed
          </span>
        )}
      </div>

      {/* Remaining manual issues */}
      {result.remainingIssues.length > 0 && (
        <div className="text-left mt-6">
          <h4 className="text-sm font-semibold text-gray-900 mb-2">Still needs attention:</h4>
          <div className="space-y-2">
            {result.remainingIssues.map((issue, i) => (
              <div key={i} className="flex items-start gap-2 text-sm p-3 bg-gray-50 rounded-lg">
                <AlertCircle className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-gray-700">{issue.description}</p>
                  <p className="text-xs text-gray-500 mt-0.5">{issue.recommendation}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <button
        onClick={onReset}
        className="mt-6 flex items-center justify-center gap-2 px-6 py-3 bg-orange-500 text-white rounded-xl font-medium hover:bg-orange-600 transition-colors mx-auto"
      >
        <RefreshCw className="w-5 h-5" />
        Run New Audit
      </button>
    </div>
  );
};

// ─── Error View ────────────────────────────────────────────────

const ErrorView: React.FC<{ message: string; onReset: () => void }> = ({ message, onReset }) => (
  <div className="py-8 px-6 text-center">
    <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-4" />
    <h3 className="text-lg font-semibold text-gray-900 mb-2">Something went wrong</h3>
    <p className="text-sm text-gray-600 mb-6">{message}</p>
    <button
      onClick={onReset}
      className="flex items-center justify-center gap-2 px-6 py-3 bg-orange-500 text-white rounded-xl font-medium hover:bg-orange-600 transition-colors mx-auto"
    >
      Try Again
    </button>
  </div>
);

// ─── Main Component ────────────────────────────────────────────

export const RoarySEOAuditPanel: React.FC<RoarySEOAuditPanelProps> = ({
  status,
  auditReport,
  fixResult,
  fixProgress,
  error,
  analyzedPages,
  onStartAudit,
  onStartFix,
  onCancel,
  onReset,
  onClose,
  isDepleted,
}) => {
  return (
    <div className="fixed inset-0 z-50 overflow-hidden">
      <div className="absolute inset-0 bg-black bg-opacity-50" onClick={onClose} />

      <div className="absolute right-0 top-0 h-full w-full max-w-xl bg-white shadow-xl flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <div className="flex items-center gap-2">
            <img src={roaryMascot} alt="Roary" className="w-6 h-6 object-contain" />
            <h2 className="text-lg font-semibold text-gray-900">Roary SEO Inspector</h2>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto">
          {status === 'idle' && <IdleView onStart={onStartAudit} isDepleted={isDepleted} />}

          {status === 'auditing' && (
            <AuditingView analyzedPages={analyzedPages} onCancel={onCancel} />
          )}

          {status === 'report' && auditReport && (
            <ReportView
              report={auditReport}
              onStartFix={() => onStartFix(auditReport.auditId)}
              onReset={onReset}
              isDepleted={isDepleted}
            />
          )}

          {status === 'fixing' && (
            <FixingView
              progress={fixProgress}
              totalFixes={auditReport?.fixableCount || 0}
              onCancel={onCancel}
            />
          )}

          {status === 'complete' && fixResult && (
            <CompleteView result={fixResult} onReset={onReset} />
          )}

          {status === 'error' && (
            <ErrorView message={error || 'An unexpected error occurred'} onReset={onReset} />
          )}
        </div>
      </div>
    </div>
  );
};
